import React, { useState, useEffect } from 'react';
import { Gift, Users, Crown, Zap, CheckCircle, Clock, TrendingUp, Award } from 'lucide-react';

const FoundersWaitlist = () => {
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    phone: '',
    referral_code: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [queuePosition, setQueuePosition] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [countdown, setCountdown] = useState({ days: 7, hours: 12, minutes: 30, seconds: 45 });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchStats();
    // Check for referral code in URL
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get('ref');
    if (refCode) {
      setFormData(prev => ({ ...prev, referral_code: refCode }));
    }

    // Countdown timer (placeholder - would be real launch date)
    const timer = setInterval(() => {
      setCountdown(prev => {
        let { days, hours, minutes, seconds } = prev;
        if (seconds > 0) {
          seconds--;
        } else if (minutes > 0) {
          minutes--;
          seconds = 59;
        } else if (hours > 0) {
          hours--;
          minutes = 59;
          seconds = 59;
        } else if (days > 0) {
          days--;
          hours = 23;
          minutes = 59;
          seconds = 59;
        }
        return { days, hours, minutes, seconds };
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/founders/stats`);
      const data = await response.json();
      if (data.ok) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/founders/waitlist/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (data.ok) {
        setQueuePosition(data.queue_position);
        setSubmitted(true);
      }
    } catch (error) {
      console.error('Error joining waitlist:', error);
      alert('Failed to join waitlist. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-800 to-blue-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-12 max-w-2xl w-full text-center">
          <div className="mb-6">
            <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-16 w-16 text-green-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">You're In! 🎉</h1>
            <div className="text-6xl font-bold text-purple-600 mb-4">#{queuePosition}</div>
            <p className="text-xl text-gray-700 mb-6">
              That's your spot in line for BBC Quick List Founder status
            </p>
          </div>

          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-6 mb-6">
            <h3 className="font-semibold text-gray-900 mb-3 text-lg">What Happens Next?</h3>
            <ul className="text-left space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="text-purple-600 mr-2">✅</span>
                <span>Check your email - we sent you a confirmation with your founder benefits</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-600 mr-2">✅</span>
                <span>Share your referral link to move up the list faster</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-600 mr-2">✅</span>
                <span>We'll notify you 48 hours before launch</span>
              </li>
            </ul>
          </div>

          <div className="border-t border-gray-200 pt-6">
            <p className="text-gray-600 mb-4">Know someone who should be a Founder?</p>
            <button
              onClick={() => {
                const url = `${window.location.origin}/founders-waitlist`;
                navigator.clipboard.writeText(url);
                alert('Link copied! Share it with friends.');
              }}
              className="bg-purple-600 text-white px-8 py-3 rounded-lg hover:bg-purple-700 font-medium"
            >
              Copy Referral Link
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-800 to-blue-900">
      {/* Hero Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white mb-12">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              👑 Become a BBC Quick List<br />
              <span className="text-yellow-400">Founder</span>
            </h1>
            <p className="text-2xl md:text-3xl mb-8 text-indigo-100">
              Exclusive lifetime benefits for the first 1,000 members
            </p>

            {/* Countdown Timer */}
            <div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-2xl p-8 inline-block">
              <p className="text-lg text-indigo-200 mb-4">🚀 Launch Countdown</p>
              <div className="grid grid-cols-4 gap-4 text-center">
                <div>
                  <div className="text-5xl font-bold">{countdown.days}</div>
                  <div className="text-sm text-indigo-200 mt-1">Days</div>
                </div>
                <div>
                  <div className="text-5xl font-bold">{countdown.hours}</div>
                  <div className="text-sm text-indigo-200 mt-1">Hours</div>
                </div>
                <div>
                  <div className="text-5xl font-bold">{countdown.minutes}</div>
                  <div className="text-sm text-indigo-200 mt-1">Minutes</div>
                </div>
                <div>
                  <div className="text-5xl font-bold">{countdown.seconds}</div>
                  <div className="text-sm text-indigo-200 mt-1">Seconds</div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Bar */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
              <div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-xl p-6 text-center text-white">
                <div className="text-3xl font-bold">{1000 - stats.total_founders}</div>
                <div className="text-sm text-indigo-200">Spots Remaining</div>
              </div>
              <div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-xl p-6 text-center text-white">
                <div className="text-3xl font-bold">{stats.total_founders}</div>
                <div className="text-sm text-indigo-200">Founders Joined</div>
              </div>
              <div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-xl p-6 text-center text-white">
                <div className="text-3xl font-bold">{stats.waitlist_count}</div>
                <div className="text-sm text-indigo-200">On Waitlist</div>
              </div>
              <div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-xl p-6 text-center text-white">
                <div className="text-3xl font-bold">{25 - stats.gold_founders}</div>
                <div className="text-sm text-indigo-200">Gold Spots Left</div>
              </div>
            </div>
          )}

          {/* Signup Form */}
          <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 max-w-2xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
              🎯 Reserve Your Founder Spot
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  required
                  placeholder="John Smith"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  placeholder="john@example.com"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number (Optional)
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Referral Code (Optional)
                </label>
                <input
                  type="text"
                  value={formData.referral_code}
                  onChange={(e) => setFormData({ ...formData, referral_code: e.target.value.toUpperCase() })}
                  placeholder="Enter code if you have one"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent uppercase"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-8 py-4 rounded-lg hover:from-purple-700 hover:to-indigo-700 font-bold text-lg transition-all disabled:opacity-50"
              >
                {loading ? 'Joining...' : '🚀 Join the Founders Waitlist'}
              </button>

              <p className="text-center text-sm text-gray-500 mt-4">
                🔒 Your information is secure. No spam, ever.
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* Founder Tiers */}
      <section className="py-20 bg-white bg-opacity-5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-white mb-12">
            Founder Tier Benefits
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Gold Tier */}
            <div className="bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-2xl p-8 text-gray-900 shadow-2xl transform hover:scale-105 transition-transform">
              <Crown className="h-12 w-12 mb-4" />
              <h3 className="text-2xl font-bold mb-2">Gold Founder</h3>
              <p className="text-3xl font-bold mb-4">First 25</p>
              <ul className="space-y-2 text-sm">
                <li>✅ Lifetime Pro Membership</li>
                <li>✅ Revenue Share Pool</li>
                <li>✅ 3 Custom Templates</li>
                <li>✅ Private Advisory Access</li>
                <li>✅ Early Feature Access</li>
              </ul>
              <p className="mt-4 font-semibold">$3,200+ Value</p>
            </div>

            {/* Silver Tier */}
            <div className="bg-gradient-to-br from-gray-300 to-gray-500 rounded-2xl p-8 text-gray-900 shadow-2xl">
              <Award className="h-12 w-12 mb-4" />
              <h3 className="text-2xl font-bold mb-2">Silver Founder</h3>
              <p className="text-3xl font-bold mb-4">First 100</p>
              <ul className="space-y-2 text-sm">
                <li>✅ 1 Year Pro Membership</li>
                <li>✅ 1 Custom Template</li>
                <li>✅ Priority Support (2hr)</li>
                <li>✅ Beta Feature Access</li>
              </ul>
              <p className="mt-4 font-semibold">$1,100+ Value</p>
            </div>

            {/* Bronze Tier */}
            <div className="bg-gradient-to-br from-orange-400 to-orange-600 rounded-2xl p-8 text-white shadow-2xl">
              <Gift className="h-12 w-12 mb-4" />
              <h3 className="text-2xl font-bold mb-2">Bronze Founder</h3>
              <p className="text-3xl font-bold mb-4">First 500</p>
              <ul className="space-y-2 text-sm">
                <li>✅ 6 Months Pro Access</li>
                <li>✅ Priority Support (4hr)</li>
                <li>✅ Exclusive Training</li>
                <li>✅ Founder Badge</li>
              </ul>
              <p className="mt-4 font-semibold">$380+ Value</p>
            </div>

            {/* Starter Tier */}
            <div className="bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl p-8 text-white shadow-2xl">
              <Users className="h-12 w-12 mb-4" />
              <h3 className="text-2xl font-bold mb-2">Starter Founder</h3>
              <p className="text-3xl font-bold mb-4">First 1,000</p>
              <ul className="space-y-2 text-sm">
                <li>✅ 3 Months Free Access</li>
                <li>✅ Founder Badge</li>
                <li>✅ Community Access</li>
                <li>✅ Founding Member</li>
              </ul>
              <p className="mt-4 font-semibold">$90+ Value</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Footer */}
      <section className="py-16 text-center text-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-4xl font-bold mb-6">Don't Miss Your Chance</h2>
          <p className="text-xl mb-8 text-indigo-200">
            Once all 1,000 founder spots are filled, these benefits will never be available again.
          </p>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className="inline-block bg-yellow-400 text-gray-900 px-12 py-4 rounded-full hover:bg-yellow-300 font-bold text-lg shadow-2xl"
          >
            Secure My Founder Spot →
          </a>
        </div>
      </section>
    </div>
  );
};

export default FoundersWaitlist;
